import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ScrollToTop from '../../../components/whatsapp connect';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleUp = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const drawCheck = keyframes`
  0% { stroke-dashoffset: 48; }
  100% { stroke-dashoffset: 0; }
`;

const Container = styled.div`
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  padding: 40px 20px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const SuccessCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  text-align: center;
  max-width: 600px;
  width: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, #13A289, #3BB77E);
  }
`;

const IconWrapper = styled.div`
  margin-bottom: 30px;
  animation: ${scaleUp} 0.5s ease-out 0.2s backwards;
`;

const Title = styled.h1`
  color: #253D4E;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
  font-family: 'Quicksand', sans-serif;
`;

const Subtitle = styled.p`
  color: #7E7E7E;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 40px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button`
  padding: 12px 30px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  min-width: 180px;

  &.primary {
    background-color: #3BB77E;
    color: white;
    box-shadow: 0 4px 15px rgba(59, 183, 126, 0.3);

    &:hover {
      background-color: #29A56C;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(59, 183, 126, 0.4);
    }
  }

  &.secondary {
    background-color: transparent;
    border-color: #3BB77E;
    color: #3BB77E;

    &:hover {
      background-color: #3BB77E;
      color: white;
      transform: translateY(-2px);
    }
  }
`;

const InfoBox = styled.div`
  background: #f0fdf4;
  border: 1px solid #dcfce7;
  border-radius: 12px;
  padding: 20px;
  text-align: left;
  margin-top: 20px;

  h4 {
    color: #166534;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    color: #15803d;
    font-size: 13px;
    margin: 0;
    line-height: 1.5;
  }
`;

// Styled Path for Animation
const AnimatedCheckPath = styled.path`
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: ${drawCheck} 0.5s ease-out 0.6s forwards;
`;

const CheckIcon = () => (
  <svg width="80" height="80" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="22" fill="#d1fae5" />
    <circle cx="24" cy="24" r="22" stroke="#3BB77E" strokeWidth="2" strokeOpacity="0.2" />
    <AnimatedCheckPath
      d="M14 24L21 31L34 17"
      stroke="#3BB77E"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

function PaymentSuccess() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <ToastContainer />
      <main className="main">
        <div className="page-header breadcrumb-wrap">
          <div className="container">
            <div className="breadcrumb">
              <div className="breadcrumb-item d-inline-block">
                <a href="/" title="Home">Home</a>
              </div>
              <div className="breadcrumb-item d-inline-block active">
                Payment Success
              </div>
            </div>
          </div>
        </div>

        <Container>
          <SuccessCard>
            <IconWrapper>
              <CheckIcon />
            </IconWrapper>

            <Title>Order Placed Successfully!</Title>
            <Subtitle>
              Thank you for choosing Kerala Special. Your order has been confirmed and will be shipped shortly.
            </Subtitle>

            <ButtonGroup>
              <Button
                className="secondary"
                onClick={() => navigate('/')}
              >
                Continue Shopping
              </Button>
              <Button
                className="primary"
                onClick={() => navigate('/myorders')}
              >
                View My Orders
              </Button>
            </ButtonGroup>

            <InfoBox>
              <h4><InfoIcon /> Cancellation Policy</h4>
              <p>
                If you wish to cancel your order, please do so within 12 hours of placing it.
                Cancellations are only processed within this time frame to ensure timely shipping.
              </p>
            </InfoBox>
          </SuccessCard>
        </Container>
        <ScrollToTop />
      </main>
    </>
  );
}

export default PaymentSuccess;
